import { Component } from "@angular/core";
import { Router } from "@angular/router";
import { OpsPlanningWorkOrderSummaryPdfRowStatus } from "@enums/opsPlanningWorkOrderSummaryPdfRowStatus.enum";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { BulkGenerateWorkOrdersPdf } from "@interfaces/bulkGenerateWorkOrdersPdf.interface";
import { BulkGenerateWorkOrdersPdfLineItem } from "@interfaces/bulkGenerateWorkOrdersPdfLineItem.interface";
import { Client } from "@interfaces/models/client.interface";
import { GenerateOpsPlanningWorkOrdersPdf } from "@interfaces/generateOpsPlanningWorkOrdersPdf.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { WorkOrder } from "@interfaces/workOrder.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ClientService } from "@services/client.service";
import { ExportLogService } from "@services/exportLog.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { WorkOrderService } from "@services/workOrder.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "workOrders",
	styleUrls: ["workOrders.css"],
	templateUrl: "workOrders.html"
})
export class WorkOrdersAdminComponent {
	bulkWorkOrderLineItems: BulkGenerateWorkOrdersPdfLineItem[] = [];
	canExportGrid = false;
	clients: Client[] = [];
	deleteEnabled = true;
	editEnabled = true;
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [{ icon: "fal fa-download", method: "generateAndDownloadSummaryPDFPopup", text: "Summary PDF" }];
	isExecutive = false;
	itemType = "Work Order"
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	items: any[] = [];
	popupAddButtonOptions: any;
	popupBulkGenerateWorkOrdersPdf: BulkGenerateWorkOrdersPdf = new BulkGenerateWorkOrdersPdf();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupGenerateOpsPlanningPdf: GenerateOpsPlanningWorkOrdersPdf = new GenerateOpsPlanningWorkOrdersPdf();
	popupTitle = "";
	popupVisible = false;
	showCompleted = false;
	statuses: any;
	title: string;
	workOrderStatuses: any;

	constructor(private appSettingsService: AppSettingsService, private toastService: ToastService, private router: Router, private clientService: ClientService, private exportLogService: ExportLogService, private itemService: WorkOrderService, private protectedGridService: ProtectedGridService) {
		this.title = `${this.itemType}s`;
		this.viewRow = this.viewRow.bind(this);
		this.editRow = this.editRow.bind(this);
		this.completeWorkOrder = this.completeWorkOrder.bind(this);
		this.deleteRow = this.deleteRow.bind(this);
		this.generateAndDownloadWorkOrderMasterLabelPDF = this.generateAndDownloadWorkOrderMasterLabelPDF.bind(this);
		this.getClients();
		this.isExecutive = CheckGroupPermissions(appSettingsService, "Executive");
		this.getItems(this.showCompleted);
		this.workOrderStatuses = GetPropertiesFromEnum(OpsPlanningWorkOrderSummaryPdfRowStatus);
		this.checkGridExportPermissions();
	}

	addNewItem() {
		this.router.navigate([`${this.itemUrl}/create`]);
	}

	buildButtons(showCompleted: boolean) {
		this.headerPrimaryButtons = [
			{ icon: "fal fa-download", method: "generateAndDownloadSummaryPDFPopup", text: "Summary PDF" },
			{ icon: "fal fa-download", method: "generateAndDownloadBulkMasterLabelsPDFPopup", text: "Master Labels PDF" }
		];

		if (showCompleted) {
			this.headerPrimaryButtons.push({ method: "showIncomplete", text: "Show Incomplete" });
		}
		else {
			this.headerPrimaryButtons.push({ method: "showComplete", text: "Show Complete" });
		}
		if (this.isExecutive) {
			this.headerPrimaryButtons.push({ icon: "fal fa-download", method: "generateAndDownloadCompleteWorkOrdersReportPDF", text: "Complete Work Orders Report" });
			this.headerPrimaryButtons.push({ icon: "fal fa-download", method: "generateAndDownloadIncompleteWorkOrdersReportPDF", text: "Incomplete Work Orders Report" });
		}
	}
	
	checkGridExportPermissions() {
		this.protectedGridService.canExport("Work Orders")
			.subscribe(
				(res: any) => {
					this.canExportGrid = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	closePopup() {
		this.popupVisible = false;
	}

	completeWorkOrder(e: any) {
		const workOrder = e.row.data;
		if (workOrder.workOrderComplete === true) {
			alert("Work Order is already Complete");
			return;
		}
		if (confirm("Are you sure you wish to mark this work order as complete? This will complete all stages in this work order, though inspections will still need adding. This cannot be undone.") === false) {
			return;
		}

		// Call the increment endpoint
		this.itemService.complete(workOrder.id)
			.subscribe(
				() => {
					this, this.getItems(this.showCompleted);
					this.toastService.success("Successfully Completed Work Order");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	deleteRow(e: any) {
		const result = confirm("Are you sure you want to delete this work order? Please be aware this will also delete any linked, child and parent work orders connected to it. This will fail if any quantity in any linked work order has been completed.");
		if (result) {
			this.itemService.deleteSingleById(e.row.data.id)
				.subscribe(
					() => {
						this.toastService.success(this.itemType + " deleted!");
						this.getItems(this.showCompleted);
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
	}

	editRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}_edit`]);
	}

	generateAndDownloadBulkMasterLabelsPDF() {
		this.popupBulkGenerateWorkOrdersPdf.workOrders = JSON.stringify(this.bulkWorkOrderLineItems);
		this.itemService.generateAndDownloadBulkMasterLabelsPdf(this.popupBulkGenerateWorkOrdersPdf).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "Work_Order_Master Labels.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
				this.closePopup();
			}
		);
	}

	generateAndDownloadBulkMasterLabelsPDFPopup() {
		this.bulkWorkOrderLineItems = [];
		this.items.forEach((workOrder: WorkOrder) => {
			const lineItem = new BulkGenerateWorkOrdersPdfLineItem();
			lineItem.includeInPdf = false;
			lineItem.workOrderId = workOrder.id;
			lineItem.dateRequired = workOrder.dateRequired;
			lineItem.partNumber = workOrder.part.partNumber;
			lineItem.quantity = workOrder.quantity;
			this.bulkWorkOrderLineItems.push(lineItem);
		});
		this.popupBulkGenerateWorkOrdersPdf = new BulkGenerateWorkOrdersPdf();
		this.popupTitle = "Generate Bulk Master Labels PDF";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.generateAndDownloadBulkMasterLabelsPDF(), text: "Generate PDF" };
	}

	generateAndDownloadCompleteWorkOrdersReportPDF() {
		this.itemService.generateAndDownloadCompleteWorkOrdersReportPdf().subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "Complete Work Orders Report.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	generateAndDownloadIncompleteWorkOrdersReportPDF() {
		this.itemService.generateAndDownloadIncompleteWorkOrdersReportPdf().subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "Incomplete Work Orders Report.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	generateAndDownloadSummaryPDF() {
		this.itemService.opsPlanningWorkOrderSummaryPdf(this.popupGenerateOpsPlanningPdf).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "Work_Order_Summary.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
				this.closePopup();
			}
		);
	}

	generateAndDownloadSummaryPDFPopup() {
		this.popupGenerateOpsPlanningPdf = new GenerateOpsPlanningWorkOrdersPdf();
		this.popupTitle = "Generate Summary PDF";
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.generateAndDownloadSummaryPDF(), text: "Generate Summary PDF" };
	}

	generateAndDownloadWorkOrderMasterLabelPDF(e: any) {
		const workOrderId = e.row.data.id;
		this.itemService.generateAndDownloadMasterLabelPdf(workOrderId).subscribe(
			(response: any) => {
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, { type: dataType }));
				downloadLink.setAttribute("download", "Work_Order_Master_Label_" + workOrderId + ".pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			},
			(err) => {
				console.log(err);
				this.toastService.error("Something Went Wrong!");
			},
		);
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => this.clients = res.response.items,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	getItems(showCompleted: boolean) {
		this.gridLoading = true;
		this.items = [];
		if (showCompleted === true) {
			this.itemService.getAllComplete()
				.subscribe(
					(res: any) => {
						this.items = res.response;
						this.gridLoading = false;
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		} else {
			this.itemService.getAllIncomplete()
				.subscribe(
					(res: any) => {
						this.items = res.response;
						this.gridLoading = false;
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
		this.buildButtons(showCompleted);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "Work Orders";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					return false;
				},
			);
	}

	showComplete() {
		this.showCompleted = true;
		this.getItems(true);
	}

	showIncomplete() {
		this.showCompleted = false;
		this.getItems(false);
	}

	viewRow(e: any) {
		this.router.navigate([`${this.itemUrl}/${e.row.data.id}`]);
	}
}